import type { SettableOfferRequestReportStatus } from '@houzy/schemas/offer-request-management';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { OFFER_REQUEST_SETTABLE_STATUS_OPTIONS, offerRequestStatusLabel } from '../labels';
import type { OfferRequestStatus } from '../types';

export type OfferRequestStatusSelectProps = {
    status: OfferRequestStatus;
    disabled?: boolean;
    tone?: 'primary' | 'neutral';
    onStatusChange?: (status: SettableOfferRequestReportStatus) => void;
};

const TONE = {
    primary: { bg: 'primary.main', bgHover: 'primary.dark', fg: 'primary.contrastText' },
    neutral: { bg: 'grey.300', bgHover: 'grey.400', fg: 'text.primary' },
} as const;

export function OfferRequestStatusSelect({
    status,
    disabled = false,
    tone = 'primary',
    onStatusChange,
}: OfferRequestStatusSelectProps): ReactElement {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const { bg, bgHover, fg } = TONE[tone];

    return (
        <>
            <Button
                variant="text"
                disabled={disabled}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                endIcon={<KeyboardArrowDownIcon />}
                aria-label={t('OPEN_REQUESTS.CARD.STATUS_ARIA')}
                sx={{
                    borderRadius: 100,
                    px: 1.5,
                    py: 0,
                    minHeight: 32,
                    fontSize: 13,
                    fontWeight: 600,
                    lineHeight: '18px',
                    bgcolor: bg,
                    color: fg,
                    '&:hover': { bgcolor: bgHover },
                    '&.Mui-disabled': { bgcolor: bg, color: fg, opacity: 0.38 },
                }}>
                {offerRequestStatusLabel(t, status)}
            </Button>
            <Menu anchorEl={anchorEl} open={anchorEl !== null} onClose={() => setAnchorEl(null)}>
                {OFFER_REQUEST_SETTABLE_STATUS_OPTIONS.map((option) => (
                    <MenuItem
                        key={option.value}
                        selected={option.value === status}
                        onClick={() => {
                            onStatusChange?.(option.value);
                            setAnchorEl(null);
                        }}>
                        {t(option.labelKey)}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
